import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { fetchSystems, type SystemDef } from '@/api/systems';

/**
 * The floor-heating system roster, fetched once per session from /api/systems.
 *
 * Deliberately has NO built-in list: before this store the same 12 systems were
 * copy-pasted into a dozen dropdowns, guards and colour maps, and every new system
 * meant editing all of them. The list now lives in Odoo; anything here that cannot
 * be answered from the fetched roster degrades (humanized label, muted dot) instead
 * of falling back to a second copy that would drift again.
 */
interface SystemsState {
  systems: SystemDef[];
  loaded: boolean;
  loading: boolean;
  /** Fetch once per session; concurrent callers share the in-flight request. */
  ensureSystems: () => Promise<void>;
  /** Force a re-fetch (after a system was added in Odoo). */
  reloadSystems: () => Promise<void>;
}

let inFlight: Promise<void> | null = null;

async function load(set: (partial: Partial<SystemsState>) => void): Promise<void> {
  set({ loading: true });
  try {
    const data = await fetchSystems();
    set({ systems: data.systems, loaded: true, loading: false });
  } catch {
    // Leave whatever we had; the consumers render an empty/legacy-only dropdown
    // rather than a wrong one, and the next mount retries.
    set({ loading: false });
  }
}

export const useSystemsStore = create<SystemsState>((set, get) => ({
  systems: [],
  loaded: false,
  loading: false,

  ensureSystems: async () => {
    if (get().loaded) return;
    inFlight ??= load(set).finally(() => { inFlight = null; });
    return inFlight;
  },

  reloadSystems: async () => {
    inFlight = load(set).finally(() => { inFlight = null; });
    return inFlight;
  },
}));

// ── Derivations (pure, so they can be used outside React too) ───────────────────

/** "noppeplaten15" → "Noppeplaten 15" — the fallback when the roster has not loaded. */
export function humanizeSystemCode(code: string): string {
  const spaced = code.replace(/([a-z])(\d)/gi, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return spaced.length > 0 ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}

export function systemLabel(code: string | null | undefined): string {
  const normalized = (code ?? '').trim().toLowerCase();
  if (!normalized) return '';
  const found = useSystemsStore.getState().systems.find(s => s.code === normalized);
  return found?.label ?? humanizeSystemCode(normalized);
}

/** Tailwind dot class for a system; muted for anything the roster does not know. */
export function systemDot(code: string | null | undefined): string {
  const normalized = (code ?? '').trim().toLowerCase();
  const found = useSystemsStore.getState().systems.find(s => s.code === normalized);
  return found?.dot ?? 'bg-td-muted';
}

/** True if Odoo accepts this value on a kring — the import wizard's save guard. */
export function isKringSystem(code: string | null | undefined): boolean {
  const normalized = (code ?? '').trim().toLowerCase();
  return useSystemsStore.getState().systems.some(s => s.code === normalized && s.kring);
}

/**
 * True when the PO flow must ask for a typed €/m²: the system has no placement
 * variants of its own (ADR-060 manual-priced path). Unknown/blank systems are manual-
 * priced, matching the backend.
 */
export function isManualPricedSystem(code: string | null | undefined): boolean {
  const normalized = (code ?? '').trim().toLowerCase();
  return !useSystemsStore.getState().systems.some(s => s.code === normalized && s.priced);
}

// ── React helpers ───────────────────────────────────────────────────────────────

/** Systems selectable on a kring, loading the roster on first mount. */
export function useKringSystems(): SystemDef[] {
  const systems = useSystems();
  return systems.filter(s => s.kring);
}

/** Systems selectable on a collector (includes "meerdere systemen"). */
export function useCollectorSystems(): SystemDef[] {
  const systems = useSystems();
  return systems.filter(s => s.collector);
}

/** Systems with their own onderaannemer price rows. */
export function usePricedSystems(): SystemDef[] {
  const systems = useSystems();
  return systems.filter(s => s.priced);
}

/**
 * Colour-dot lookup that re-renders when the roster arrives. Unknown systems (legacy
 * values in old data, a system removed in Odoo) get the muted dot.
 */
export function useSystemDot(): (code: string | null | undefined) => string {
  const systems = useSystems();
  return useMemo(() => {
    const byCode = new Map(systems.map(s => [s.code, s.dot]));
    return (code) => byCode.get((code ?? '').trim().toLowerCase()) ?? 'bg-td-muted';
  }, [systems]);
}

/** Label lookup that re-renders when the roster arrives. */
export function useSystemLabel(): (code: string | null | undefined) => string {
  const systems = useSystems();
  return useMemo(() => {
    const byCode = new Map(systems.map(s => [s.code, s.label]));
    return (code) => {
      const normalized = (code ?? '').trim().toLowerCase();
      if (!normalized) return '';
      return byCode.get(normalized) ?? humanizeSystemCode(normalized);
    };
  }, [systems]);
}

/** The whole roster, loading it on first mount. */
export function useSystems(): SystemDef[] {
  const systems = useSystemsStore(s => s.systems);
  const ensureSystems = useSystemsStore(s => s.ensureSystems);
  useEffect(() => { void ensureSystems(); }, [ensureSystems]);
  return systems;
}
